import React from 'react';
import PeopleIcon from "@mui/icons-material/People";
import AvTimerIcon from "@mui/icons-material/AvTimer";
import DateRangeIcon from '@mui/icons-material/DateRange';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AssignmentIcon from "@mui/icons-material/Assignment";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import GavelIcon from '@mui/icons-material/Gavel';
import PaidIcon from '@mui/icons-material/Paid';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HistoryIcon from '@mui/icons-material/History';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TuneIcon from '@mui/icons-material/Tune';
import BlockIcon from '@mui/icons-material/Block';

/**
 * hasRole — Helper to determine if a user has access to a menu section.
 */
export const hasRole = (user, allowedRoles) => {
    if (!user || !user.rolName) return false;
    return allowedRoles.includes(user.rolName);
};

/**
 * menuConfig — Centralized navigation configuration.
 */
export const menuConfig = [
    // SECTION: SYSTEM USERS (SuperAdmin only)
    {
        id: 'users',
        title: 'Usuarios del Sistema',
        icon: <ManageAccountsIcon />,
        roles: ['SuperAdmin'],
        link: '/users/list'
    },

    // SECTION: VOID MANAGEMENT (SuperAdmin only)
    {
        id: 'void',
        title: 'Anular Comprobante',
        icon: <BlockIcon />,
        roles: ['SuperAdmin'],
        link: '/consumptions/void'
    },

    // SECTION: CONFIGURATION — SuperAdmin + Admin (con Facturación)
    {
        id: 'config',
        title: 'Configuración',
        icon: <SettingsSuggestIcon />,
        roles: ['SuperAdmin', 'Admin'],
        children: [
            { title: "Institución",    icon: <BusinessIcon />,  link: "/institution/list" },
            { title: "Clasificación",  icon: <CategoryIcon />,  link: "/classification/list" },
            { title: "Crear Aportes",  icon: <PaidIcon />,      link: "/contribution/list" },
            { title: "Crear Multa",    icon: <GavelIcon />,     link: "/fine/list" },
            { title: "Facturación",    icon: <TuneIcon />,      link: "/billing/config" },
        ]
    },

    // SECTION: CONFIGURATION — Secretary (sin Facturación)
    {
        id: 'config-secretary',
        title: 'Configuración',
        icon: <SettingsSuggestIcon />,
        roles: ['Secretary'],
        children: [
            { title: "Crear Aportes", icon: <PaidIcon />,  link: "/contribution/list" },
            { title: "Crear Multa",   icon: <GavelIcon />, link: "/fine/list" },
        ]
    },

    // SECTION: CONFIGURATION — Read-only (Directivo)
    {
        id: 'config-directivo',
        title: 'Configuración',
        icon: <SettingsSuggestIcon />,
        roles: ['Directivo'],
        children: [
            { title: "Institución",   icon: <BusinessIcon />, link: "/institution/list" },
            { title: "Clasificación", icon: <CategoryIcon />, link: "/classification/list" },
            { title: "Aportes",       icon: <PaidIcon />,     link: "/contribution/list" },
            { title: "Multas",        icon: <GavelIcon />,    link: "/fine/list" },
        ]
    },

    // SECTION: ADMINISTRATION — Full (SuperAdmin, Admin)
    {
        id: 'admin',
        title: 'Administración',
        icon: <AdminPanelSettingsIcon />,
        roles: ['SuperAdmin', 'Admin'],
        children: [
            { title: "Socios",               icon: <PeopleIcon />,   link: "/associate/list" },
            { title: "Cambios de Socios",    icon: <HistoryIcon />,  link: "/associate/changelog" },
            { title: "Medidores",            icon: <AvTimerIcon />,  link: "/measurer/list" },
            { title: "Consumo",          icon: <WaterDropIcon />,        link: "/consumption/list" },
            { title: "Agregar Aporte",   icon: <PaidIcon />,             link: "/contributionAdd/list" },
            { title: "Agregar Multa",    icon: <GavelIcon />,            link: "/fineAdd/list" },
            { title: "Transferencias",   icon: <SwapHorizIcon />,        link: "/transferred/register" },
            { title: "Agregar Restante", icon: <AddCircleOutlinedIcon />, link: "/passive/add" },
            { title: "Limpiar Errores",  icon: <DeleteSweepIcon />,      link: "/clean/error" },
            { title: "Amnistía",         icon: <EventRepeatIcon />,      link: "/amnesty/list" },
        ]
    },

    // SECTION: ADMINISTRATION — Secretary (sin Medidores, Transferencias ni Limpiar Errores)
    {
        id: 'admin-secretary',
        title: 'Administración',
        icon: <AdminPanelSettingsIcon />,
        roles: ['Secretary'],
        children: [
            { title: "Socios",           icon: <PeopleIcon />,           link: "/associate/list" },
            { title: "Consumo",          icon: <WaterDropIcon />,        link: "/consumption/list" },
            { title: "Agregar Aporte",   icon: <PaidIcon />,             link: "/contributionAdd/list" },
            { title: "Agregar Multa",    icon: <GavelIcon />,            link: "/fineAdd/list" },
            { title: "Amnistía",         icon: <EventRepeatIcon />,      link: "/amnesty/list" },
        ]
    },

    // SECTION: ADMINISTRATION — Read-only (Directivo)
    {
        id: 'admin-directivo',
        title: 'Administración',
        icon: <AdminPanelSettingsIcon />,
        roles: ['Directivo'],
        children: [
            { title: "Socios",    icon: <PeopleIcon />,    link: "/associate/list" },
            { title: "Medidores", icon: <AvTimerIcon />,   link: "/measurer/list" },
            { title: "Consumo",   icon: <WaterDropIcon />, link: "/consumption/list" },
            { title: "Amnistía",  icon: <EventRepeatIcon />, link: "/amnesty/list" },
        ]
    },

    // SECTION: PAYMENTS (Admin)
    {
        id: 'payments-admin',
        title: 'Pagos por Transferencia',
        icon: <AccountBalanceIcon />,
        roles: ['SuperAdmin', 'Admin', 'Secretary'],
        children: [
            { title: 'Solicitudes',        icon: <PendingActionsIcon />, link: '/payment/requests' },
            { title: 'Configurar método',  icon: <AccountBalanceIcon />, link: '/payment/methods' },
        ]
    },

    // SECTION: ACCOUNTANCY
    {
        id: 'accountancy',
        title: 'Contabilidad',
        icon: <AccountBalanceWalletIcon />,
        roles: ['SuperAdmin', 'Admin', 'Tesorero'],
        link: "/accounting"
    },

    // SECTION: REPORTS
    {
        id: 'reports',
        title: 'Reportes',
        icon: <AssignmentIcon />,
        roles: ['SuperAdmin', 'Admin', 'Tesorero', 'Directivo'],
        children: [
            {
                title: "Cobros",
                icon: <PointOfSaleIcon />,
                link: "/reports/charges"
            },
            {
                title: "Consumos",
                icon: <WaterDropIcon />,
                link: "/reports/consumptions"
            },
            {
                title: "Medidores",
                icon: <AvTimerIcon />,
                link: "/reports/measures"
            },
            {
                title: "Medidores sin Lectura Mensual",
                icon: <WaterDropIcon />,
                link: "/reports/measurer"
            },
            {
                title: "Deudores por Meses",
                icon: <DateRangeIcon />,
                link: "/reports/measurerCut"
            },
            {
                title: "Detalles de pago",
                icon: <ReceiptIcon />,
                link: "/reports/measurerPay"
            },
            {
                title: "Reporte de Transferencias",
                icon: <SwapHorizIcon />,
                link: "/reports/transferAssociate"
            },
        ]
    },

    // SECTION: ATTENDANCE
    {
        id: 'attendance',
        title: 'Asistencia',
        icon: <EventAvailableIcon />,
        roles: ['SuperAdmin', 'Admin', 'Secretary', 'Directivo'],
        children: [
            { title: "Eventos", icon: <DateRangeIcon />, link: "/attendance/events" },
            { title: "Registrar", icon: <HowToRegIcon />, link: "/attendance/register" },
            { title: "Control", icon: <FactCheckIcon />, link: "/attendance/control" },
            { title: "Administración", icon: <AdminPanelSettingsIcon />, link: "/attendance/admin" },
            { title: "Última Asistencia", icon: <HistoryIcon />, link: "/attendance/last" },
        ]
    },

    // SECTION: READER
    {
        id: 'reader',
        title: 'Lecturador',
        icon: <AppRegistrationIcon />,
        roles: ['SuperAdmin', 'Reader'],
        children: [
            { title: "Lecturas", icon: <AvTimerIcon />, link: "/roleReader/list" },
            { title: "Sin Lectura del Mes", icon: <WaterDropIcon />, link: "/reports/measurer" },
        ]
    },

    // SECTION: ASSOCIATE
    {
        id: 'associate',
        title: 'Mi Cuenta',
        icon: <AccountCircleIcon />,
        roles: ['Associate'],
        link: '/roleAssociate/dashboard'
    },

    // SECTION: PAYMENTS (Associate)
    {
        id: 'payments-associate',
        title: 'Mis Transferencias',
        icon: <AccountBalanceIcon />,
        roles: ['Associate'],
        link: '/roleAssociate/payment-requests'
    }
];
